import React, { useMemo } from 'react'
import { Info } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts'
import { Card, Tooltip } from '../../ui'
import { AreaSlice, PublishedBroadcast } from '../../../services/broadcastService'
import { rateFor, valueOf, formatMoney, formatAreaSeconds } from '../broadcastValuation'

interface Props {
  /** What the page is showing. The slices are summed over these. */
  broadcasts: PublishedBroadcast[]
  /** The whole library, unfiltered. Only used to fix colours, never to sum. */
  all: PublishedBroadcast[]
}

// Categorical slots in fixed order, then a neutral for the folded tail.
const SLOTS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300']
const OTHER_COLOR = '#b4b2a9'
const MAX_SLICES = 6

export type SlicePick = (b: PublishedBroadcast) => AreaSlice[] | undefined

interface Slice { key: string; label: string; amount: number; color: string }

/**
 * Sum one breakdown over a set of broadcasts.
 *
 * PRICED PER BROADCAST. Each slice is valued at its own broadcast's 30-second
 * rate before it is added, the same way the Media value card is built, so the
 * donut totals to that card. Unpriced broadcasts contribute nothing in value
 * mode — they would otherwise pull the shares toward whichever games happen
 * to be missing an audience.
 */
const sumSlices = (broadcasts: PublishedBroadcast[], pick: SlicePick, mode: 'value' | 'area') => {
  const acc = new Map<string, { label: string; amount: number }>()
  for (const b of broadcasts) {
    const rate = rateFor(b)
    if (mode === 'value' && rate === null) continue
    for (const s of pick(b) ?? []) {
      const amount = mode === 'value' ? (valueOf(s.area_seconds, rate).value ?? 0) : s.area_seconds
      const prev = acc.get(s.key)
      acc.set(s.key, { label: prev?.label ?? s.label ?? 'Unclassified', amount: (prev?.amount ?? 0) + amount })
    }
  }
  return acc
}

/**
 * COLOUR FOLLOWS THE ENTITY, NOT ITS RANK. Slots are handed out by each key's
 * standing across the whole library, so searching the table narrows a donut
 * without repainting the sponsors that survive the filter.
 */
export const colourMap = (all: PublishedBroadcast[], pick: SlicePick) => {
  const totals = sumSlices(all, pick, 'area')
  const order = [...totals.entries()].sort((a, b) => b[1].amount - a[1].amount)
  const m = new Map<string, string>()
  order.slice(0, MAX_SLICES - 1).forEach(([k], i) => m.set(k, SLOTS[i]))
  return m
}

/**
 * At most six segments. The five largest keep their own slice and everything
 * after folds into Other — a donut stops being readable well before a
 * season's worth of sponsors.
 */
const toSlices = (acc: Map<string, { label: string; amount: number }>, colours: Map<string, string>): Slice[] => {
  const ranked = [...acc.entries()]
    .filter(([, v]) => v.amount > 0)
    .sort((a, b) => b[1].amount - a[1].amount)
  const named = ranked.filter(([k]) => colours.has(k))
  const rest = ranked.filter(([k]) => !colours.has(k))
  const out: Slice[] = named.map(([k, v]) => ({ key: k, label: v.label, amount: v.amount, color: colours.get(k)! }))
  if (rest.length === 1) {
    const [k, v] = rest[0]
    out.push({ key: k, label: v.label, amount: v.amount, color: OTHER_COLOR })
  } else if (rest.length > 1) {
    out.push({
      key: '__other',
      label: `Other (${rest.length})`,
      amount: rest.reduce((n, [, v]) => n + v.amount, 0),
      color: OTHER_COLOR
    })
  }
  return out
}

interface DonutProps {
  title: string
  tooltip: string
  slices: Slice[]
  format: (n: number) => string
}

const Donut: React.FC<DonutProps> = ({ title, tooltip, slices, format }) => {
  const total = slices.reduce((n, s) => n + s.amount, 0)
  const pct = (n: number) => total > 0 ? `${((n / total) * 100).toFixed(0)}%` : '—'

  return (
    <Card padding="md">
      <div className="flex items-center gap-1.5 mb-3">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <Tooltip content={tooltip} placement="top" maxWidth={260}>
          <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
        </Tooltip>
      </div>

      {slices.length === 0 ? (
        <p className="text-xs text-gray-400 py-10 text-center">No sponsored exposure in view.</p>
      ) : (
        <div className="flex items-center gap-5">
          <div className="relative h-44 w-44 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="amount"
                  nameKey="label"
                  innerRadius="66%"
                  outerRadius="100%"
                  startAngle={90}
                  endAngle={-270}
                  stroke="#ffffff"
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  {slices.map((s) => <Cell key={s.key} fill={s.color} />)}
                </Pie>
                <RechartsTooltip
                  formatter={(v: number, name: string) => [`${format(v)} · ${pct(v)}`, name]}
                  contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#e5e7eb' }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* The total in the hole: the donut answers "of what", so the
                what sits where the eye lands. */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Total</span>
              <span className="text-base font-bold text-gray-900 tabular-nums">{format(total)}</span>
            </div>
          </div>

          <ul className="flex-1 min-w-0 space-y-1.5">
            {slices.map((s) => (
              <li key={s.key} className="flex items-center gap-2 text-xs">
                <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
                <span className="truncate text-gray-700">{s.label}</span>
                <span className="ml-auto tabular-nums font-medium text-gray-900">{format(s.amount)}</span>
                <span className="w-9 text-right tabular-nums text-gray-500">{pct(s.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

/**
 * Where the library's sponsor value went: to whom, and from which positions.
 *
 * VALUE WHEN ANYTHING IS PRICED, AREA WHEN NOTHING IS. A library with no
 * audiences recorded still has a real split of exposure worth showing; the
 * titles say which one is drawn rather than drawing area under a money label.
 */
const BroadcastValueDonuts: React.FC<Props> = ({ broadcasts, all }) => {
  const priced = broadcasts.some((b) => rateFor(b) !== null)
  const mode = priced ? 'value' : 'area'
  const currency = broadcasts.find((b) => rateFor(b) !== null)?.currency ?? 'USD'

  const sponsorColours = useMemo(() => colourMap(all, (b) => b.sponsor_area), [all])
  const zoneColours = useMemo(() => colourMap(all, (b) => b.zone_area), [all])

  const sponsors = useMemo(
    () => toSlices(sumSlices(broadcasts, (b) => b.sponsor_area, mode), sponsorColours),
    [broadcasts, mode, sponsorColours]
  )
  const zones = useMemo(
    () => toSlices(sumSlices(broadcasts, (b) => b.zone_area, mode), zoneColours),
    [broadcasts, mode, zoneColours]
  )

  if (broadcasts.length === 0) return null

  const format = mode === 'value'
    ? (n: number) => formatMoney(n, currency)
    : (n: number) => formatAreaSeconds(n)
  const measure = mode === 'value' ? 'value' : 'area-seconds'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <Donut
        title={mode === 'value' ? 'Value delivered by sponsor' : 'Exposure by sponsor'}
        tooltip={mode === 'value'
          ? 'Sponsor value across the broadcasts in view, each priced at its own 30-second rate. Broadcasts with no audience recorded are left out.'
          : 'No broadcast in view is priced yet, so this splits sponsored area-seconds instead of value.'}
        slices={sponsors}
        format={format}
      />
      <Donut
        title={mode === 'value' ? 'Sponsor Value by placement' : 'Exposure by placement'}
        tooltip={`Sponsored ${measure} by placement — outfield fence, backstop, broadcast graphics and so on.`}
        slices={zones}
        format={format}
      />
    </div>
  )
}

export default BroadcastValueDonuts
